<template>
    <section id="about" class="about">
        <div class="ob-arc about-arc-1" />
        <div class="ob-arc about-arc-2" />

        <div class="about-grid">
            <div>
                <div class="ob-signal about-signal">
                    <span class="asig-1" />
                    <span class="asig-2" />
                    <span class="asig-3" />
                    <span class="asig-4" />
                    <span class="asig-5" />
                </div>
                <div class="about-eyebrow">About us</div>
                <h2 class="about-h2">We Are One.</h2>
                <p class="about-p">Reliable internet as an everyday utility in Sierra Leone — designed with you, by a Freetown team.</p>
                <div class="about-stats">
                    <div v-for="s in WHO_STATS" :key="s.k">
                        <div class="about-stat-v">{{ s.v }}</div>
                        <div class="about-stat-k">{{ s.k }}</div>
                    </div>
                </div>
            </div>

            <!-- company / team slideshow — placeholders until real photography arrives -->
            <div>
                <div class="slideshow">
                    <div
                        v-for="(p, i) in PHOTOS"
                        :key="p.id"
                        class="ob-slide"
                        :class="{ 'is-on': i === store.slide }"
                    >
                        <div class="slide-placeholder">
                            <span class="slide-ph-icon">◎</span>
                            <span>{{ p.placeholder }}</span>
                        </div>
                    </div>
                </div>
                <div class="slide-footer">
                    <div class="slide-caption">{{ PHOTOS[store.slide]?.caption }}</div>
                    <div class="slide-dots">
                        <button
                            v-for="(p, i) in PHOTOS"
                            :key="p.id"
                            aria-label="Show photo"
                            class="slide-dot"
                            :class="{ active: i === store.slide }"
                            @click="store.slide = i"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import { PHOTOS, WHO_STATS } from '@/data/content'
    import { useLandingStore } from '@/stores/landing'

    const store = useLandingStore()
</script>

<style scoped>
    .about { position: relative; max-width: 1240px; margin: 0 auto; padding: 0 44px 88px; }
    .about-arc-1 { position: absolute; right: -90px; top: -40px; width: 300px; height: 300px; pointer-events: none; }
    .about-arc-2 { position: absolute; right: -40px; top: 10px; width: 200px; height: 200px; pointer-events: none; }
    .about-grid { position: relative; display: grid; grid-template-columns: 1fr 1fr; gap: 52px; align-items: center; }

    .about-signal { display: flex; flex-direction: column; gap: 5px; margin-bottom: 24px; }
    .asig-1 { width: 118px; background: var(--ob-sky); }
    .asig-2 { width: 70px; background: var(--ob-blue); }
    .asig-3 { width: 96px; background: var(--ob-blue-deep); }
    .asig-4 { width: 52px; background: var(--ob-magenta); }
    .asig-5 { width: 80px; background: var(--ob-pink); }

    .about-eyebrow { font-size: 12px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: var(--ob-blue-deep); }
    .about-h2 {
        margin: 16px 0 0;
        font-size: clamp(38px, 5.2vw, 72px);
        font-weight: 800;
        letter-spacing: -.036em;
        line-height: .94;
        color: var(--ob-ink);
    }
    .about-p { margin: 22px 0 0; max-width: 420px; font-size: 17.5px; line-height: 1.55; color: var(--ob-ink-soft); }
    .about-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; margin-top: 34px; padding-top: 28px; border-top: 1px solid var(--ob-navy-a14); }
    .about-stat-v {
        font-size: clamp(24px, 2.6vw, 34px);
        font-weight: 800;
        letter-spacing: -.03em;
        line-height: 1;
        background: var(--ob-grad-brand-120);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
    }
    .about-stat-k { margin-top: 9px; font-size: 13.5px; font-weight: 600; color: var(--ob-ink); }

    .slideshow {
        position: relative;
        border-radius: 26px;
        overflow: hidden;
        aspect-ratio: 4 / 3;
        box-shadow: var(--ob-shadow-photo);
        background: var(--ob-photo-bg);
    }
    .slide-placeholder {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        font-size: 14.5px;
        font-weight: 600;
        color: var(--ob-ink-mid);
    }
    .slide-ph-icon { font-size: 30px; color: var(--ob-blue-deep); opacity: .5; }
    .slide-footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 18px; }
    .slide-caption { font-size: 13.5px; color: var(--ob-ink-sub); }
    .slide-dots { display: flex; gap: 8px; }
    .slide-dot {
        cursor: pointer;
        border: none;
        padding: 0;
        height: 8px;
        width: 8px;
        border-radius: 999px;
        transition: all .25s;
        background: var(--ob-navy-a22);
    }
    .slide-dot.active { width: 26px; background: var(--ob-blue); }

    @media (max-width: 1024px) {
        .about-grid { grid-template-columns: 1fr; gap: 36px; }
        .about-arc-1, .about-arc-2 { display: none; }
    }
    @media (max-width: 700px) {
        .about { padding: 0 20px 56px; }
        .about-stats { gap: 14px; }
    }
</style>
